import classNames from 'classnames';
import React from 'react';

import { Spinner } from 'app/src/components/Spinner';
import { HeaderTestIds } from '@trello/webdriverio-tests';
import { forNamespace, forTemplate } from 'app/common/lib/util/i18n';
import {
  Key,
  registerShortcut,
  Scope,
  unregisterShortcut,
} from '@trello/keybindings';
import { isMac, isDesktop } from '@trello/browser';
import { shouldHandleWindowsFrame } from '@trello/desktop';
import { Noun, Verb, trackUe } from '@trello/analytics';

import BoardList from './board-list';
import styles from './boards-menu.less';
import BoardsSearch from './boards-search';
import { BoardsMenuContainerProps } from './types';
import { BoardsMenuButton } from './boards-menu-button';

const format = forTemplate('boards_sidebar');
const formatRaw = forNamespace();
const track = (verb: Verb.OPENS | Verb.CLOSES, method: string) =>
  trackUe({
    category: Noun.HEADER,
    verb,
    directObj: Noun.BOARDS_MENU,
    method,
  });

const popoverWasOpened = (
  prev: BoardsMenuContainerProps,
  next: BoardsMenuContainerProps,
): boolean => {
  if (prev.alwaysOpenBoardsMenu || next.alwaysOpenBoardsMenu) {
    return false;
  }
  return !prev.isBoardsMenuOpen && next.isBoardsMenuOpen;
};

const popoverWasClosed = (
  prev: BoardsMenuContainerProps,
  next: BoardsMenuContainerProps,
): boolean => {
  if (prev.alwaysOpenBoardsMenu || next.alwaysOpenBoardsMenu) {
    return false;
  }
  return prev.isBoardsMenuOpen && !next.isBoardsMenuOpen;
};

const menuWasPinned = (
  prev: BoardsMenuContainerProps,
  next: BoardsMenuContainerProps,
): boolean => {
  return !prev.alwaysOpenBoardsMenu && next.alwaysOpenBoardsMenu;
};

const menuWasUnpinned = (
  prev: BoardsMenuContainerProps,
  next: BoardsMenuContainerProps,
): boolean => {
  return prev.alwaysOpenBoardsMenu && !next.alwaysOpenBoardsMenu;
};

export class BoardsMenu extends React.Component<BoardsMenuContainerProps> {
  ref = React.createRef<HTMLDivElement>();
  searchRef = React.createRef<HTMLInputElement>();

  componentDidMount() {
    document.addEventListener('click', this.onOutsideClick, true);
    registerShortcut(this.onBKeypress, { scope: Scope.Header, key: Key.b });
  }

  componentDidUpdate(prevProps: BoardsMenuContainerProps) {
    if (popoverWasOpened(prevProps, this.props)) {
      this.handleFocusOnSearch();
      registerShortcut(this.onEscapeKeypress, {
        scope: Scope.Popover,
        key: Key.Escape,
      });
    } else if (popoverWasClosed(prevProps, this.props)) {
      this.resetState();
      unregisterShortcut(this.onEscapeKeypress);
    } else if (menuWasPinned(prevProps, this.props)) {
      unregisterShortcut(this.onEscapeKeypress);
    } else if (menuWasUnpinned(prevProps, this.props)) {
      registerShortcut(this.onEscapeKeypress, {
        scope: Scope.Popover,
        key: Key.Escape,
      });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.onOutsideClick);
    unregisterShortcut(this.onEscapeKeypress);
    unregisterShortcut(this.onBKeypress);
  }

  handleFocusOnSearch() {
    setImmediate(() => {
      if (this.searchRef && this.searchRef.current) {
        this.searchRef.current.focus();
      }
    });
  }

  // Since we're not unmounting when we toggle the visible state,
  // of the popover, we need to reset this state
  // to it's defaults when hiding the boards menu
  resetState() {
    const { clearSearchText, clearSelectedBoard } = this.props;

    setTimeout(() => {
      if (this.searchRef && this.searchRef.current) {
        this.searchRef.current.value = '';
      }
      clearSearchText();
      clearSelectedBoard();
    });
  }

  onBKeypress: React.KeyboardEventHandler<HTMLElement> = (e) => {
    const {
      alwaysOpenBoardsMenu,
      isBoardsMenuOpen,
      toggleMenu,
      idMe,
    } = this.props;

    // Do not attempt to open the boards menu unless the user is
    // logged in
    if (!idMe) {
      return;
    }

    if (!alwaysOpenBoardsMenu) {
      toggleMenu();

      if (!isBoardsMenuOpen) {
        track(Verb.OPENS, 'by using boards menu shortcut');
      } else {
        track(Verb.CLOSES, 'by using boards menu shortcut');
      }
    } else {
      this.handleFocusOnSearch();
    }
  };

  onEscapeKeypress: React.KeyboardEventHandler<HTMLElement> = (e) => {
    const { alwaysOpenBoardsMenu, isBoardsMenuOpen, toggleMenu } = this.props;

    if (isBoardsMenuOpen && !alwaysOpenBoardsMenu) {
      toggleMenu();
      track(Verb.CLOSES, 'by pressing escape key');
    }
  };

  onOutsideClick = (e: MouseEvent) => {
    const { isBoardsMenuOpen, headerButtonMouseOver, toggleMenu } = this.props;

    if (isBoardsMenuOpen && headerButtonMouseOver) {
      e.stopPropagation();
    }

    if (
      isBoardsMenuOpen &&
      this.ref.current &&
      !this.ref.current.contains(e.target as Node)
    ) {
      toggleMenu();
      track(Verb.CLOSES, 'by clicking outside popover');
    }
  };

  render() {
    const {
      alwaysOpenBoardsMenu,
      categoriesWithBoards,
      toggleMenu,
      filteredBoards,
      isBoardsMenuOpen,
      isSearching,
      loading,
      searchText,
      selectedBoard,
      showCreateBoard,
      toggleAlwaysOpenBoardsMenu,
      toggleClosedBoardDialog,
      visibleBoards,
    } = this.props;

    return (
      <div
        ref={this.ref}
        className={classNames(
          styles.boardsDrawer,
          !alwaysOpenBoardsMenu && styles.boardsDrawerPopover,
          shouldHandleWindowsFrame() && styles.desktopWin,
        )}
        hidden={!alwaysOpenBoardsMenu && !isBoardsMenuOpen}
      >
        <div
          className={classNames(
            styles.boardsDrawerHeader,
            isDesktop() && isMac() && styles.desktopMac,
          )}
          hidden={!alwaysOpenBoardsMenu}
        >
          {format('boards')}
        </div>
        <div className={styles.boardsMenuWrapper}>
          <div
            className={styles.boardsMenu}
            data-test-id={HeaderTestIds.BoardsMenuPopover}
          >
            <BoardsSearch
              ref={this.searchRef}
              boards={filteredBoards}
              searchText={searchText}
              selectedBoard={selectedBoard}
              visibleBoards={visibleBoards}
              isBoardsMenuOpen={isBoardsMenuOpen}
              closeBoardsMenu={toggleMenu}
            />
            <BoardList
              categories={categoriesWithBoards}
              hidden={isSearching || loading}
              alwaysOpenBoardsMenu={alwaysOpenBoardsMenu}
              closeBoardsMenu={toggleMenu}
            />
            {loading && <Spinner>{format('boards')}</Spinner>}
            <div className={styles.buttons}>
              <BoardsMenuButton
                onClick={showCreateBoard}
                className={classNames(isSearching && styles.createBoardButton)}
                testId={HeaderTestIds.BoardsMenuCreateBoard}
              >
                {isSearching
                  ? formatRaw('add board named', { boardName: searchText })
                  : format('create-new-board-ellipsis')}
              </BoardsMenuButton>

              {!isSearching && (
                <>
                  <BoardsMenuButton
                    onClick={() =>
                      toggleAlwaysOpenBoardsMenu(alwaysOpenBoardsMenu)
                    }
                    testId={HeaderTestIds.BoardsMenuPin}
                  >
                    {!alwaysOpenBoardsMenu
                      ? format('always-keep-this-menu-open')
                      : format('don-t-keep-this-menu-open')}
                  </BoardsMenuButton>
                  <BoardsMenuButton
                    onClick={toggleClosedBoardDialog}
                    testId={HeaderTestIds.BoardsMenuOpenClosed}
                  >
                    {format('see-closed-boards-ellipsis')}
                  </BoardsMenuButton>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
